import React, { useState, useEffect } from "react";
import { Table, Typography, Button } from "antd";
import { EyeOutlined } from "@ant-design/icons";

const { Text } = Typography;

const OfferSelectionTable = ({
  offerOptions,
  onSelectOffer,
  selectedOffer,
  disabled, // Add disabled prop
  onViewOfferDetails, // Add handler for viewing offer details
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState(
    selectedOffer ? [selectedOffer.id] : []
  );

  useEffect(() => {
    if (selectedOffer) {
      setSelectedRowKeys([selectedOffer.id]);
    } else {
      setSelectedRowKeys([]);
    }
  }, [selectedOffer]);

  const expandedRowRender = (record) => {
    const marketingSKUs = record.marketing_skus || [];
    const rows = [];

    marketingSKUs.forEach((sku) => {
      (sku.marketing_dependent_skus_details || []).forEach((detail, index) => {
        rows.push({
          key: `${sku.id}-${index}`,
          sku: sku.sku,
          detail,
        });
      });
    });

    const columns = [
      {
        title: "Marketing SKU",
        dataIndex: "sku",
        key: "sku",
      },
      {
        title: "Variation Code",
        dataIndex: ["detail", "variation_code"],
        key: "variation_code",
      },
      {
        title: "Description",
        dataIndex: ["detail", "description"],
        key: "description",
      },
      {
        title: "Qty",
        dataIndex: ["detail", "quantity"],
        key: "quantity",
      },
      {
        title: "Qty Detailed",
        dataIndex: ["detail", "quantity_detailed"],
        key: "quantity_detailed",
      },
      {
        title: "Unit Description",
        dataIndex: ["detail", "unit_description"],
        key: "unit_description",
      },
      {
        title: "Original Price",
        dataIndex: ["detail", "original_price"],
        key: "original_price",
        render: (val) => `$${parseFloat(val).toFixed(2)}`,
      },
      {
        title: "Final Price",
        dataIndex: ["detail", "final_price"],
        key: "final_price",
        render: (val) => `$${parseFloat(val).toFixed(2)}`,
      },
      {
        title: "Discount",
        dataIndex: ["detail", "discount"],
        key: "discount",
        render: (val) => (val || "-"),
      },
    ];

    return (
      <Table
        columns={columns}
        dataSource={rows}
        pagination={false}
        rowKey="key"
        size="small"
      />
    );
  };

  const columns = [
    {
      title: "Brand",
      dataIndex: "brand_name",
      key: "brand_name",
    },
    {
      title: "Status",
      dataIndex: ["status", "name"],
      key: "status",
    },
    {
      title: "Code Offer",
      dataIndex: "code_offer",
      key: "code_offer",
    },
    {
      title: "Type",
      dataIndex: ["type_of_offer", "type_of_offer"],
      key: "type_of_offer",
    },
    {
      title: "Form",
      dataIndex: ["form_of_offer", "form_of_offer"],
      key: "form_of_offer",
    },
    {
      title: "Gift Visible",
      dataIndex: "gift_visible_in_offer",
      key: "gift_visible_in_offer",
    },
    {
      title: "SKUs",
      key: "skus",
      render: (record) => {
        const count = (record.marketing_skus || []).reduce(
          (sum, sku) => sum + (sku.marketing_dependent_skus_details?.length || 0),
          0
        );
        return `${count} item(s)`;
      },
    },
    {
      title: "Product Variations",
      key: "product_variations",
      render: (record) =>
        (record.product_variations_detail || [])
          .map((v) => v.description)
          .join(", "),
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      render: (_, record) => (
        <Button
          icon={<EyeOutlined />}
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onViewOfferDetails && onViewOfferDetails(record);
          }}
        />
      ),
    },
  ];

  const rowSelection = disabled ? null : {
    type: "radio",
    selectedRowKeys,
    onChange: (selectedKeys, selectedRows) => {
      setSelectedRowKeys(selectedKeys);
      if (selectedRows.length > 0) {
        onSelectOffer(selectedRows[0]); // Pass the full offer object!
      }
    },
  };

  return (
    <Table
      rowSelection={rowSelection}
      columns={columns}
      dataSource={offerOptions}
      expandable={{
        expandedRowRender,
        rowExpandable: (record) =>
          record.marketing_skus?.some(
            (sku) => sku.marketing_dependent_skus_details?.length
          ),
      }}
      className="small-font"
      size="small"
      pagination={false}
      rowKey="id"
      scroll={{ x: "max-content" }}
    />
  );
};

export default OfferSelectionTable;